import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  Timer, CheckCircle, Zap, AlertTriangle, BarChart2, Play, Cpu, Clock4
} from 'lucide-react';
import { AgentProps } from '../Interfaces/AgentProps';

const COLORS = ['#4ade80', '#f87171', '#facc15'];

const metrics = [
  { icon: <Play className="w-4 h-4 text-indigo-500" />, label: 'Total Runs', value: '3,210' },
  { icon: <CheckCircle className="w-4 h-4 text-green-500" />, label: 'Success Rate', value: '97.4%' },
  { icon: <Timer className="w-4 h-4 text-yellow-400" />, label: 'Avg Response Time', value: '1.21s' },
  { icon: <Zap className="w-4 h-4 text-purple-400" />, label: 'Tokens Used (24h)', value: '34,520' },
];

const runHistory = [
  { time: '10 AM', runs: 120 },
  { time: '11 AM', runs: 220 },
  { time: '12 PM', runs: 300 },
  { time: '1 PM', runs: 180 },
  { time: '2 PM', runs: 260 },
  { time: '3 PM', runs: 400 },
];

const tokenLatency = [
  { time: '10 AM', tokens: 3000, latency: 1.2 },
  { time: '11 AM', tokens: 4200, latency: 1.4 },
  { time: '12 PM', tokens: 3800, latency: 1.3 },
  { time: '1 PM', tokens: 2900, latency: 1.0 },
  { time: '2 PM', tokens: 4500, latency: 1.7 },
];

const outcomeDistribution = [
  { name: 'Success', value: 85 },
  { name: 'Failure', value: 10 },
  { name: 'Warnings', value: 5 },
];
type Props = {
  agent?: AgentProps;
};

const AgentMetricsDashboard: React.FC<Props> = ({ agent }) => {
  return (
    <div className="p-6 space-y-6 text-white bg-neutral-950">
      {/* Heading */}
      <div className="flex items-center gap-2 mb-4">
        <Cpu className="w-5 h-5 text-indigo-500" />
        <h2 className="text-lg font-semibold">Agent Metrics Overview</h2>
      </div>

      {/* Metric Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
        {metrics.map((m, i) => (
          <div
            key={i}
            className="bg-neutral-900 border border-neutral-800 p-3 rounded-md flex items-center gap-3"
          >
            {m.icon}
            <div>
              <div className="text-gray-400 text-[11px]">{m.label}</div>
              <div className="text-white text-sm font-medium">{m.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Line Chart - Run History */}
      <div className="bg-neutral-900 border border-neutral-800 rounded-md p-4">
        <div className="flex items-center gap-2 mb-2">
          <BarChart2 className="w-4 h-4 text-blue-400" />
          <h3 className="text-sm font-medium">Runs Per Hour</h3>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={runHistory}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
            <XAxis dataKey="time" stroke="#888" />
            <YAxis stroke="#888" />
            <Tooltip />
            <Line type="monotone" dataKey="runs" stroke="#6366f1" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Bar Chart - Tokens vs Latency */}
      <div className="bg-neutral-900 border border-neutral-800 rounded-md p-4">
        <div className="flex items-center gap-2 mb-2">
          <Clock4 className="w-4 h-4 text-purple-400" />
          <h3 className="text-sm font-medium">Token Usage vs Latency</h3>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={tokenLatency}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
            <XAxis dataKey="time" stroke="#888" />
            <YAxis yAxisId="left" stroke="#4ade80" />
            <YAxis yAxisId="right" orientation="right" stroke="#818cf8" />
            <Tooltip />
            <Bar yAxisId="left" dataKey="tokens" fill="#4ade80" />
            <Bar yAxisId="right" dataKey="latency" fill="#818cf8" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Pie Chart - Outcome Distribution */}
      <div className="bg-neutral-900 border border-neutral-800 rounded-md p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-yellow-500" />
          <h3 className="text-sm font-medium">Response Outcome Distribution</h3>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie
              data={outcomeDistribution}
              cx="50%"
              cy="50%"
              outerRadius={80}
              label
              dataKey="value"
            >
              {outcomeDistribution.map((entry, index) => (
                <Cell key={index} fill={COLORS[index]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AgentMetricsDashboard;
